import { Divider } from '@design-system';
import classNames from 'classnames';
import { motion, useScroll, useTransform } from 'framer-motion';
import type { TFunction } from 'i18next';
import { useTranslation } from 'next-i18next';
import SbEditable from 'storyblok-react';

import { ArtistSearchResult, AugmentedArtistCard } from '../../types/Artists';
import {
  ReleaseSubscriptionCtaStoryblokExtended,
  StickySubscriptionCtaStoryblokExtended,
} from '../../types/Franchises';
import { ReleasePage, ReleasePageData } from '../../types/ReleaseContentType';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { AnalyticsObjectProvider } from '../../utils/analytics/object';
import RelatedArtists from '../Content/RelatedArtists';
import { ContentBlocks } from '../Journals';
import { ArticleHeadingReleases } from '../Journals/ArticleHeadingReleases';
import PageSection from '../Layout/PageSection';
import { ShareButtons } from '../Share/ShareButtons';

const getReleaseLabel = ({
  status,
  t,
}: {
  status?: 'upcoming' | 'register' | 'live';
  t: TFunction;
}): string => {
  switch (status) {
    case 'live':
      return t('releases:status.live');
    case 'register':
      return t('releases:status.register');
    default:
      return t('releases:status.upcoming');
  }
};

interface Props {
  analytics: Analytics;
  data: ReleasePageData;
  page: ReleasePage;
  internalArtists: ArtistSearchResult[];
  relatedArtists: AugmentedArtistCard[];
  isHeaderHidden?: boolean;
}

export const ReleasePageContent = ({
  analytics,
  data,
  internalArtists,
  isHeaderHidden = false,
  page,
  relatedArtists,
}: Props) => {
  const { t } = useTranslation(['artists', 'releases']);
  const [analyticsEmitter] = useAnalyticsEmitter(analytics);

  // Sticky effect for the hero section
  const { scrollY } = useScroll();
  const opacity = useTransform(scrollY, [0, 480], [1, 0]);
  const scale = useTransform(scrollY, [0, 480], [1, 0.9]);

  const sections = page.content?.article || [];

  const productListId = (sections || []).find(
    (s) => s.component === 'productList',
  )?._uid;

  const sectionsExtended = sections
    .filter(
      (article, index, arr) =>
        article.component !== 'stickySubscriptionCTA' ||
        arr.findIndex((a) => a.component === 'stickySubscriptionCTA') === index,
    )
    .map((article) => {
      if (article.component === 'releaseSubscriptionCTA') {
        return {
          ...article,
          subscriptionId: page.uuid, // Pass the page UUID as the subscription ID
          subscriptionArtists: internalArtists,
        } as ReleaseSubscriptionCtaStoryblokExtended;
      }

      if (article.component === 'stickySubscriptionCTA') {
        return {
          ...article,
          subscriptionId: page.uuid, // Pass the page UUID as the subscription ID
          subscriptionArtists: internalArtists,
          productListId, // Pass the _uid of the first productList found inside the page
          title: page.content.title, // Pass data down the component
          subtitle: getReleaseLabel({ status: page.content.releaseStatus, t }), // Pass data down the component
          releaseStatus: page.content.releaseStatus, // Pass data down the component
        } as StickySubscriptionCtaStoryblokExtended;
      }

      return { ...article, analytics };
    });

  return (
    // @ts-expect-error require storyblok-react upgrade
    <SbEditable key={page.content._uid} content={page.content}>
      <main className="my-9 flex flex-col gap-y-9 md:my-12 md:gap-y-12">
        <div
          className={classNames(
            'sticky -z-10',
            isHeaderHidden
              ? 'top-[36px] md:top-[48px]'
              : 'top-[96px] md:top-[120px]',
          )}
        >
          <PageSection>
            <motion.div
              className="sticky top-30 flex flex-col items-center justify-center text-center"
              style={{ opacity, scale }}
            >
              <ArticleHeadingReleases
                description={page.content.description}
                onThumbnailClick={() => {
                  analyticsEmitter('release page', 'hero artist clicked');
                }}
                releaseStatus={page.content.releaseStatus}
                resolvedArtists={data.resolvedArtists}
                subtitle={page.content.subtitle}
                thumbnail={page.content.thumbnail}
                title={page.content.title}
              />
            </motion.div>
          </PageSection>
        </div>

        {sectionsExtended && sectionsExtended?.length > 0 ? (
          <PageSection>
            <AnalyticsObjectProvider name="release page" properties={analytics}>
              {() => <ContentBlocks content={sectionsExtended} />}
            </AnalyticsObjectProvider>
          </PageSection>
        ) : null}

        <PageSection>
          <div className="mx-auto flex w-full max-w-lg flex-col items-center space-y-3">
            <div className="flex flex-col items-center text-center">
              <p className="font-bold">{t('releases:share.title')}</p>
              <p>{t('releases:share.subtitle')}</p>
            </div>
            <ShareButtons
              onClick={(platform) => analyticsEmitter('share', platform)}
              text=""
              utmCampaign="release-page"
            />
          </div>
        </PageSection>

        <div className="wrapper-website">
          <Divider className="my-9 md:my-12" />
        </div>

        <PageSection>
          <RelatedArtists
            artists={relatedArtists}
            onArtistClick={() => {
              analyticsEmitter('release page', 'related artist clicked');
            }}
            onViewAllArtistsClick={() => {
              analyticsEmitter('release page', 'all artists clicked');
            }}
            title={t('releases:discoverOtherArtists')}
          />
        </PageSection>
      </main>
    </SbEditable>
  );
};
